import React, { useMemo } from 'react';
import { StyleSheet } from 'react-native';
import { DocumentStatus } from '@alucio/aws-beacon-amplify/src/models';
import { DNABox, DNACard, DNAConditionalWrapper, DNAText, Iffy, useTheme, util } from '@alucio/lux-ui';
import {
  DNAChip,
  DNACustomFieldChipList,
  DNADocumentStatusChip,
  DNAPopover,
  DNADocumentThumbnail,
  DNARowsComponent,
} from 'src/components/DNA';
import { FolderItemORM, DocumentVersionORM } from 'src/types/types';
import { CustomLabelDisplay } from '../default/CustomLabelDisplay';
import { detectArchivedFileKeyPath } from 'src/components/SlideSelector/useThumbnailSelector';
import { canUseFolderDocument } from 'src/utils/customDeckRules/eligibility';

const styles = StyleSheet.create({
  cardStyle: {
    backgroundColor: 'transparent',
  },
  titleText: {
    marginLeft: 24,
    paddingRight: 16,
  },
  popoverAnchor: {
    flex: 1,
  },
});

const DNACustomDeckFolderItemRow: DNARowsComponent<FolderItemORM> = (props) => {
  const {
    ORM: folderItem,
    onPress,
  } = props

  const theme = useTheme()

  const documentVersionORM = (folderItem.relations.itemORM as DocumentVersionORM)
  const { status: docStatus } = documentVersionORM.relations.documentORM.model
  const { visiblePages } = folderItem.model
  const isModified =
    visiblePages?.length &&
    documentVersionORM.meta.permissions.MSLSelectSlides &&
    documentVersionORM.model.status === 'PUBLISHED'
  const isArchived = docStatus === DocumentStatus.ARCHIVED
  const isLatestPublished = documentVersionORM.meta.version.isLatestPublished
  const isInteractive = canUseFolderDocument(documentVersionORM, visiblePages)
  // If the publisher's selected cover page was hidden by the user
  // we use the first visible page
  // [TODO-2126] - We should be able calculate this at the ORM level instead
  let customThumbnailKey: string | undefined
  const selectedThumbnail = documentVersionORM.model.selectedThumbnail ?? 1
  if (folderItem.model.visiblePages && !folderItem.model.visiblePages.find(vp => vp === selectedThumbnail)) {
    const customPage = documentVersionORM.meta.allPages[folderItem.model.visiblePages[0] - 1]
    customThumbnailKey = detectArchivedFileKeyPath(documentVersionORM.model, customPage)
  }

  const allStyle = {
    paddingHorizontal: theme['row-padding-horizontal'],
    paddingVertical: theme['row-padding-vertical'],
  }

  const popoverInfoText = useMemo(() => {
    return isModified
      ? 'Modified files cannot be used when building presentations'
      : isArchived
        ? 'Archived files cannot be used when building presentations'
        : !isLatestPublished
          ? 'This file is an outdated version and cannot be used when building presentations'
          : ''
  }, [isModified, isArchived, isLatestPublished])

  return (
    <DNAConditionalWrapper
      condition={!isInteractive && !!popoverInfoText}
      wrapper={children => (
        <DNAPopover disablePopover={['tabletPWA']} placement="top">
          <DNAPopover.Anchor style={styles.popoverAnchor}>
            {children}
          </DNAPopover.Anchor>
          <DNAPopover.Content>
            <DNAText colorMode="dark">{popoverInfoText}</DNAText>
          </DNAPopover.Content>
        </DNAPopover>
      )}
    >
      <DNACard
        appearance="flat"
        interactive={isInteractive ? 'pressable' : undefined}
        disabled={!isInteractive}
        onPress={() => onPress?.(folderItem)}
        style={util.mergeStyles(props, allStyle, styles.cardStyle)}
      >
        <DNABox
          fill
          testID="folder-item-row"
          alignY="center"
          style={{ opacity: isInteractive ? 1 : 0.4 }}
        >
          <DNABox fill alignY="center">
            {/* Thumbnail */}
            <DNABox>
              <DNADocumentThumbnail.Hover
                documentVersionORM={documentVersionORM}
                width={120}
                height={68}
                customThumbnailKey={customThumbnailKey}
              />
            </DNABox>
            <DNABox fill appearance="col" spacing="sm"style={styles.titleText}>
              {/* Title */}
              <DNAText testID="document-item-title" b1 numberOfLines={1}>
                {folderItem.meta.title}
              </DNAText>
              {/* Badge & Tags */}
              <DNABox
                fill
                childFill={1}
                alignY="center"
                childStyle={[4, { flex: 1 }]}
              >
                <DNABox appearance="row" spacing="sm" alignY="center">
                  <DNACustomFieldChipList documentVersionORM={documentVersionORM} />
                  <Iffy is={isArchived}>
                    <DNADocumentStatusChip documentStatus={DocumentStatus.ARCHIVED} />
                  </Iffy>
                  <Iffy is={isModified}>
                    <DNAChip.Beacon variant="MODIFIED" />
                  </Iffy>
                </DNABox>
                <CustomLabelDisplay
                  documentORM={documentVersionORM.relations.documentORM}
                />
              </DNABox>
            </DNABox>
          </DNABox>
        </DNABox>
      </DNACard>
    </DNAConditionalWrapper>
  )
}

export default DNACustomDeckFolderItemRow
